import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import _ from "lodash";
import FuseListOfValuesModal from "../modals/FuseListOfValuesModal";
import { fetchAutoCompleteListOfValues, fetchDisplayValue, fetchModelValue } from "../../../webservices/generic/listOfValuesWs";

function FuseListOfValuesInput(props) {
  const lovTable = props?.lovTable;
  const lovKeyColumn = props?.lovKeyColumn;
  const lovValueColumn = props?.lovValueColumn;

  const isMountedRef = useRef(null);
  const inputComponent = useRef(null);

  const [displayValue, setDisplayValue] = useState("");
  const [lovAutoComplete, setLovAutoComplete] = useState([]);

  const [isValidValue, setIsValidValue] = useState(false);
  const [isShowingModal, setIsShowingModal] = useState(false);
  const [isShowingAutoComplete, setIsShowingAutoComplete] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const minAutoCompleteLength = props.minAutoCompleteLength || 3;

  const _constraints = () => {
    if (props.lovFilterKey && props.lovFilterValue)
      return [
        {
          columnName: props.lovFilterKey,
          value: props.lovFilterValue
        }
      ];
    else return [];
  };

  useEffect(() => {
    updateValidity();
  });

  useEffect(() => {
    isMountedRef.current = true;

    if (props?.value == null || props?.value === "") {
      setDisplayValue("");
    } else if (displayValue == null || displayValue === "") {
      fetchDisplayValue(lovTable, lovKeyColumn, lovValueColumn, _constraints(), props?.value)
        .then((response) => {
          if (response.data.success === true) {
            let dispValue = response.data.result;
            if (isMountedRef.current) setDisplayValue(dispValue);
          } else {
            console.error("LOV Fetch Display Value failed:", response?.data);
            props?.onChange(""); //Initial key is not a valid LOV key value
          }
        })
        .catch((error) => {
          console.error("LOV Fetch Display Value failed:", error);
          props?.onChange(""); //Initial key is not a valid LOV key value
        });
    }

    return () => (isMountedRef.current = false);
  }, [props?.value]);

  useEffect(() => {
    setIsShowingAutoComplete(
      isFocused &&
        lovAutoComplete != null &&
        Object.keys(lovAutoComplete).length > 0 &&
        displayValue != null &&
        displayValue.length >= minAutoCompleteLength
    );
  }, [isFocused, lovAutoComplete, displayValue]);

  const onButtonClicked = (e) => {
    setIsShowingModal(true);
  };

  const onModalClosed = () => {
    setIsShowingModal(false);
  };

  const onModalSubmitted = (rec) => {
    setIsShowingModal(false);
    if (rec != null) {
      setDisplayValue(rec[lovValueColumn]);
      props?.onChange(rec[lovKeyColumn]);
    } else {
      setDisplayValue("");
      props?.onChange("");
    }
  };

  const getErrorDescription = (err) => {
    if (err === "ValueMissing") return `This field is required. Please select a value.`;
    return `This field value is not valid`;
  };

  const _invalidErrorMessage = () => {
    let errors = getValidateErrors();
    if (errors.length > 0) return getErrorDescription(errors[0]);
    else return "";
  };

  const checkValid = () => getValidateErrors().length === 0;
  const getValidateErrors = () => {
    let errors = [];
    if (inputComponent?.current != null) {
      const { validity } = inputComponent.current;
      if (validity?.valueMissing === true) errors.push("ValueMissing");
      if (validity?.tooShort === true) errors.push("TooShort");
      if (validity?.tooLong === true) errors.push("TooLong");
      if (validity?.patternMismatch == true) errors.push("PatternMismatch");
      if (validity?.badInput === true) errors.push("BadInput");
      if (validity?.rangeOverflow === true) errors.push("RangeOverflow");
      if (validity?.rangeUnderflow === true) errors.push("RangeUnderflow");
      if (validity?.stepMismatch === true) errors.push("StepMismatch");
      if (validity?.typeMismatch === true) errors.push("TypeMismatch");
      // console.log(props.id, validity, errors);
    } // else if (props?.id) console.log("current == null ", props.id);
    return errors;
  };

  const updateValidity = () => {
    if (!inputComponent?.current) return;
    setIsValidValue(checkValid());
    inputComponent.current.setCustomValidity(_invalidErrorMessage());
  };

  const onFocus = (e) => {
    setIsFocused(true);
  };

  const onBlur = (e) => {
    if (e?.relatedTarget?.className == null || e.relatedTarget.className !== "dropdown-item") {
      isMountedRef.current = true;
      if (displayValue != null && displayValue !== "") {
        fetchModelValue(lovTable, lovKeyColumn, lovValueColumn, _constraints(), displayValue)
          .then((response) => {
            if (response.data.success === true && isMountedRef.current === true) {
              let keyValue = response.data.result;
              if (keyValue == null) setDisplayValue("");
              props?.onChange(keyValue || "");
            } else {
              console.error("LOV Fetch Display Value failed:", response?.data);
              if (isMountedRef.current) {
                setDisplayValue("");
                props?.onChange("");
              }
            }
          })
          .catch((error) => {
            console.error("LOV Fetch Display Value failed:", error);
            if (isMountedRef.current) {
              setDisplayValue("");
              props?.onChange("");
            }
          });
      }
      setIsFocused(false);
    }
  };

	const handleClearValue = (e) => {
		setDisplayValue("");
		props?.onChange("");
	}

  const handleChange = (e) => {
    isMountedRef.current = true;

    if (isMountedRef.current === true) setDisplayValue(e?.target?.value || "");

    if (e?.target?.value?.length >= minAutoCompleteLength) {
      fetchAutoCompleteListOfValues(lovTable, lovKeyColumn, lovValueColumn, _constraints(), e?.target?.value || "")
        .then((response) => {
          if (response.data.success === true && isMountedRef.current == true) {
            // console.log(response.data.result);
            let autoCompleteData = response.data.result;
            setLovAutoComplete([...autoCompleteData]);
          } else {
            console.error("LOV Auto completion failed:", response?.data);
            if (isMountedRef.current) setLovAutoComplete([]);
          }
        })
        .catch((error) => {
          console.error("LOV Auto completion failed:", error);
          if (isMountedRef.current) setLovAutoComplete([]);
        });
    } else if (isMountedRef.current) setLovAutoComplete([]);
  };

  if (props?.editable === true) {
    return (
      <div key={props?.id} className={props.className}>
        <FuseListOfValuesModal
          lovTable={props.lovTable}
          lovFilterKey={props.lovFilterKey}
          lovFilterValue={props.lovFilterValue}
          isShowing={isShowingModal}
          onClose={onModalClosed}
          onSubmit={onModalSubmitted}
        />
        <Form.Group as={Row} className="d-flex" className="position-relative">
          <Col className="flex-grow-1">
            <Dropdown
              show={isShowingAutoComplete}
              // menuPortalTarget={document.body}
            >
              <Dropdown.Toggle bsPrefix="p-0" as={typeof (<div />)}>
								<div className="position-relative">
									<Form.Control
										//type
										as="input"
										ref={inputComponent}
										//identifiers
										id={props.id}
										//values
										value={displayValue}
										placeholder={props?.placeholder}
										disabled={props.disabled || false}
										//validation
										isInvalid={!isValidValue}
										required={props?.required}
										//handlers
										onChange={handleChange}
										onBlur={onBlur}
										onFocus={onFocus}
									/>
								</div>
              </Dropdown.Toggle>
              <Dropdown.Menu aria-labelledby={`autoCompleteMenu-${props.id}`}>
                {lovAutoComplete?.map((item, i) => (
                  <Dropdown.Item
                    key={i}
                    onSelect={() => {
                      setDisplayValue(item.value || "");
                      props?.onChange(item?.key || "");
                      setIsFocused(false);
                    }}
                  >
                    {item.value}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
          <Col className="flex-grow-0">
            {(displayValue != null && displayValue !== "") ? 
						(<Button 	variant="light" onClick={handleClearValue} disabled={props.disabled || false}>
              <i className="fa fa-times"/>
            </Button>) : 
						(<Button variant="light" onClick={onButtonClicked} disabled={props.disabled || false}>
              <i className="fa fa-list" />
            </Button>)
						}
          </Col>
        </Form.Group>
      </div>
    );
  } else {
    return (
      <div key={props?.id} className={props.className}>
        {displayValue || ""}
      </div>
    );
  }
}

FuseListOfValuesInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  id: PropTypes.string,
  value: PropTypes.any,
  keyValue: PropTypes.any,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  lovTable: PropTypes.string.isRequired,
  lovKeyColumn: PropTypes.string.isRequired,
  lovValueColumn: PropTypes.string.isRequired,
  lovFilterKey: PropTypes.any,
  lovFilterValue: PropTypes.any,
  minAutoCompleteLength: PropTypes.number
};

export default FuseListOfValuesInput;
